import { hasValue, holdInUtc, holdPropsInUtc, PushNotification } from "@repo/helpers";
import { KeyedArray, SerializedFile } from "@repo/models";
import { useCallback } from "react";

import { useRepContext } from "../contexts/editableItemContext";
import { useSettingsQuery } from "../helpers/queryHooks";
import { DrugTest } from "../models";
import { UploadedFile } from "../models/backgroundCheck";
import { DrugTestResultEnum } from "../models/drugTest";
import * as Queries from "./queries";
import { DrugTestDocumentTypes, useRepDocumentApi } from "./repApi";
import { useQueryWrapper, useRepPortalApi } from "./repPortalApi";

export function useDrugTestApi() {
	const { Get, Post } = useRepPortalApi();
	const { saveRepDocument, deleteRepDocument } = useRepDocumentApi();
	const { repId } = useRepContext();
	const [settings] = useSettingsQuery();

	const saveDrugTest = useCallback(
		function (dt: DrugTest) {
			const {
				resultDocument,
				resultDocumentUploaded,
				secondTestResultDocument,
				secondTestResultDocumentUploaded,
				consentDocument,
				consentDocumentUploaded,
				...drugTest
			} = dt;

			return Post<number>("SaveDrugTest", null, { representativeId: repId, ...drugTest });
		},
		[Post],
	);

	const saveDrugTestDoc = useCallback(
		function (repId: number, file: SerializedFile, dtId: number, docType: DrugTestDocumentTypes) {
			return saveRepDocument(repId, file, docType, null, dtId);
		},
		[Post],
	);
	const deleteDrugTestDoc = useCallback(
		function (documentId: number) {
			return deleteRepDocument(documentId);
		},
		[Post],
	);
	const saveDrugTestDocs = useCallback(
		async function (drugTestId: number, dt: DrugTest) {
			await Promise.all([
				deleteDocument(dt.consentDocumentUploaded),
				deleteDocument(dt.resultDocumentUploaded),
				deleteDocument(dt.secondTestResultDocumentUploaded),
			]);
			await Promise.all([
				saveDocument(dt.consentDocument, DrugTestDocumentTypes.DrugTestConsentDocument),
				saveDocument(dt.resultDocument, DrugTestDocumentTypes.DrugTestResultDocument),
				saveDocument(dt.secondTestResultDocument, DrugTestDocumentTypes.DrugTestResultSecondDocument),
			]);

			async function saveDocument(files: SerializedFile[], docType: DrugTestDocumentTypes) {
				if (!files) return;
				for (const file of files) await saveDrugTestDoc(dt.representativeId, file, drugTestId, docType);
			}
			async function deleteDocument(files: UploadedFile[]) {
				if (!files) return;
				for (const file of files.filter((f) => f?.deleted)) await deleteDrugTestDoc(file.id);
			}
		},
		[saveDrugTestDoc, deleteDrugTestDoc],
	);

	const initialize = useCallback(
		function (dt: DrugTest) {
			if (settings.timesInLocal) holdPropsInUtc(dt, "createdOn");

			dt.responseStarted = hasValue(dt.secondTestConductedOn);
			dt.conductedOn = holdInUtc(dt.conductedOn);
			dt.secondTestConductedOn = holdInUtc(dt.secondTestConductedOn);
			dt.resultName = DrugTestResultEnum[dt.resultId];
			dt.secondTestResultName = DrugTestResultEnum[dt.secondTestResultId];

			if (settings.drugTestSettings?.expirationDays) {
				const dtDate = new Date(dt.conductedOn ?? dt.createdOn ?? new Date());
				const expirationDate = new Date();
				expirationDate.setDate(expirationDate.getDate() - settings.drugTestSettings.expirationDays);
				dt.expired = dtDate < expirationDate;
			}

			return dt;
		},
		[settings.timesInLocal, settings.drugTestSettings?.expirationDays],
	);

	const loadDrugTest = useCallback(
		function (drugTestId: number) {
			return Get<DrugTest>("GetDrugTest", { drugTestId }).then(initialize);
		},
		[Get, initialize],
	);
	const getDrugTests = useCallback(
		async function (repId: number) {
			const drugTests = await Get<DrugTest[]>("DrugTests", {
				representativeId: repId,
			});
			return drugTests.map(initialize);
		},
		[Get, initialize],
	);

	const copyToNewSegment = useCallback(
		async function (drugTestId: number, segmentId: number) {
			return await Post<number, null>("CopyDrugTestToSegment", {
				drugTestId,
				segmentId,
			});
		},
		[Post],
	);

	const associateToNewSegment = useCallback(
		async function (drugTestId: number, segmentId: number) {
			return await Post<number, null>("AssociateDrugTestToSegment", {
				drugTestId,
				segmentId,
			});
		},
		[Post],
	);

	const loadDrugTests = useCallback(
		function (repId: number) {
			return async function () {
				if (!repId) return defaultArr();
				try {
					const drugTests = await getDrugTests(repId);
					const keyedItems = new KeyedArray<DrugTest>(drugTests, (b) => b.drugTestId);
					return keyedItems;
				} catch (e) {
					PushNotification("Unable to retrieve drug tests");
					return defaultArr();
				}
			};
		},
		[getDrugTests],
	);

	const useDrugTestQuery = (repId: number) =>
		useQueryWrapper(Queries.DRUG_TESTS, [repId, loadDrugTests], loadDrugTests(repId));

	return {
		saveDrugTest,
		saveDrugTestDocs,
		loadDrugTest,
		getDrugTests,
		copyToNewSegment,
		associateToNewSegment,
		useDrugTestQuery,
	};
}

const defaultArr = () => new KeyedArray<DrugTest>([], (b) => b.drugTestId);
